import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { endpoints } from '@rossum/api-client';
import { Organization } from '@rossum/api-client/organization';
import { Queue } from '@rossum/api-client/queues';
import { AnnotationListColumns } from '@rossum/api-client/shared';
import { SchemaWithEtag, useSchema } from '../../../business/schema/useSchema';
import { useApiClient } from '../../../lib/apiClient';
import { safeOrganizationSelector } from '../../../redux/modules/organization/selectors';
import { getFlattenSchemaFields } from '../../schemas/getFlattenSchemaFields';
import {
  QUERY_KEY_ORGANIZATION,
  useFetchOrganization,
} from './useFetchOrganization';

// Which organization-settings field holds the table config. The all-docs
// dashboard uses `annotationListTable`; the request dashboard keeps its columns
// in a separate `requestDashboardTable` field so the two don't share state.
export type OrgTableSettingsKey =
  | 'annotationListTable'
  | 'requestDashboardTable';

const EMPTY_COLUMNS: AnnotationListColumns = [];

type Props = {
  // Defaults to the current organization when omitted, so callers in features
  // that cannot reach the redux org selector don't have to resolve it
  // themselves. Pass `0` explicitly to target the queue level.
  orgId?: number;
  queueId: number;
  settingsKey?: OrgTableSettingsKey;
  // Columns to return when no config has been saved yet. Defaults to an empty
  // table (all-docs / queue); the request dashboard passes its default schema
  // columns so legacy customers still see them.
  fallbackColumns?: AnnotationListColumns;
};
export const QUERY_KEY_USE_QUEUE_TABLE = 'queue-table-config';

const getFlattenSchemaFieldsMap = (schema: SchemaWithEtag) =>
  schema.content ? getFlattenSchemaFields(schema.content) : {};

export const useTableConfig = ({
  orgId,
  queueId,
  settingsKey = 'annotationListTable',
  fallbackColumns = EMPTY_COLUMNS,
}: Props) => {
  const api = useApiClient();
  const isQueueLevel = !!queueId;

  const currentOrgId = useSelector(safeOrganizationSelector)?.id;
  const resolvedOrgId = orgId ?? currentOrgId;

  const { data: organization, isInitialLoading: isOrgTableLoading } =
    useFetchOrganization({
      enabled: !isQueueLevel,
      orgId: resolvedOrgId,
    });

  const { data: queue, isFetching: isQueueTableLoading } = useQuery({
    queryKey: [QUERY_KEY_USE_QUEUE_TABLE, queueId],
    queryFn: () => api.request(endpoints.queues.get(queueId)),
    enabled: isQueueLevel,
  });

  const { data: schemaFieldsMap, isFetching: isSchemaFetching } = useSchema(
    queue?.schema,
    { select: getFlattenSchemaFieldsMap }
  );

  const withScoreThreshold = queue?.settings.annotationListTable?.columns.map(
    column => {
      if (
        column.columnType === 'schema' &&
        schemaFieldsMap &&
        schemaFieldsMap[column.schemaId]
      ) {
        const field = schemaFieldsMap[column.schemaId]?.field;
        const scoreThreshold =
          field && 'scoreThreshold' in field
            ? field.scoreThreshold
            : queue?.defaultScoreThreshold;

        return {
          ...column,
          scoreThreshold,
        };
      }

      return column;
    }
  );

  const fallbackTable = { columns: fallbackColumns };

  const queueTableConfig = queue?.settings.annotationListTable
    ? {
        ...queue.settings.annotationListTable,
        columns: withScoreThreshold ?? [],
      }
    : fallbackTable;
  const orgTableConfig = organization?.settings[settingsKey] ?? fallbackTable;

  return {
    columns: isQueueLevel ? queueTableConfig.columns : orgTableConfig.columns,
    isLoading: isQueueTableLoading || isSchemaFetching || isOrgTableLoading,
  };
};

export const useSetTableConfig = () => {
  const queryClient = useQueryClient();

  const setOrganizationTable = useCallback(
    (
      callback: (response: Organization | undefined) => Organization | undefined
    ) =>
      queryClient.setQueriesData<Organization | undefined>(
        [QUERY_KEY_ORGANIZATION],
        response => callback(response)
      ),
    [queryClient]
  );

  const setQueueTable = useCallback(
    (callback: (response: Queue | undefined) => Queue | undefined) =>
      queryClient.setQueriesData<Queue | undefined>(
        [QUERY_KEY_USE_QUEUE_TABLE],
        response => callback(response)
      ),
    [queryClient]
  );

  return { setOrganizationTable, setQueueTable };
};
